import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { ChatContextSelection, ClientRef } from './types'
import { EMPTY_SELECTION } from './types'

const STORAGE_PREFIX = 'credem.chat.context.'
const PENDING_KEY = `${STORAGE_PREFIX}__pending__`

function storageKey(threadId: string | null | undefined): string {
  if (!threadId) return PENDING_KEY
  return `${STORAGE_PREFIX}${threadId}`
}

function readFromStorage(key: string): ChatContextSelection | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ChatContextSelection>
    if (!parsed || typeof parsed !== 'object') return null
    const client = parsed.client && typeof parsed.client === 'object' ? (parsed.client as ClientRef) : null
    return { client }
  } catch {
    return null
  }
}

function writeToStorage(key: string, value: ChatContextSelection): void {
  if (typeof window === 'undefined') return
  try {
    if (!value.client) {
      window.sessionStorage.removeItem(key)
      return
    }
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fail silently.
  }
}

function removeFromStorage(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export interface ChatContextSelectionApi {
  selection: ChatContextSelection
  setClient: (client: ClientRef | null) => void
  clearAll: () => void
  hasSelection: boolean
  /**
   * Visible, demo-friendly context line appended to the user's message so the
   * referenced client is rendered in conversation history alongside files.
   */
  buildVisibleMessageLine: () => string | undefined
  /**
   * Hidden seed-context line forwarded as the first user message of a brand-new
   * thread. The agent system prompt parses `client_code` from this line and
   * uses it to scope tool calls.
   */
  buildSeedContextLine: () => string | undefined
  /**
   * Structured per-turn metadata mirrored as ACE Message.metadata.chat_context
   * so the same scope is available to the agent runtime even after the seed
   * line falls out of the relevant window.
   */
  buildMessageMetadata: () => { chat_context: { client_code: string } } | undefined
}

/**
 * Owns the per-thread Client context-tag selection rendered in the dashboard
 * chat composer. Sticky on the active thread via sessionStorage so the chip
 * survives page refresh and round-trips, and migrates a "pending" selection
 * to the real thread key once the SDK assigns a thread id on first send.
 */
export function useChatContextSelection(threadId: string | null | undefined): ChatContextSelectionApi {
  const [selection, setSelection] = useState<ChatContextSelection>(() => {
    const stored = readFromStorage(storageKey(threadId)) ?? readFromStorage(PENDING_KEY)
    return stored ?? EMPTY_SELECTION
  })

  const previousThreadIdRef = useRef<string | null | undefined>(threadId)

  useEffect(() => {
    const previous = previousThreadIdRef.current
    if (previous === threadId) return
    previousThreadIdRef.current = threadId

    // Thread id transitioned from pending to a real id: migrate the pending
    // selection onto the new thread key so the chip stays attached to the
    // conversation that just got created.
    if (!previous && threadId) {
      const pending = readFromStorage(PENDING_KEY)
      if (pending && pending.client) {
        writeToStorage(storageKey(threadId), pending)
        removeFromStorage(PENDING_KEY)
        setSelection(pending)
        return
      }
    }

    // Otherwise rehydrate the selection for the (possibly different) thread.
    const stored = readFromStorage(storageKey(threadId))
    setSelection(stored ?? EMPTY_SELECTION)
  }, [threadId])

  useEffect(() => {
    writeToStorage(storageKey(threadId), selection)
  }, [selection, threadId])

  const setClient = useCallback((client: ClientRef | null) => {
    setSelection((current) => {
      if (!client) return EMPTY_SELECTION
      if (current.client?.client_code === client.client_code) return current
      return { client }
    })
  }, [])

  const clearAll = useCallback(() => {
    setSelection(EMPTY_SELECTION)
  }, [])

  const buildSeedContextLine = useCallback((): string | undefined => {
    if (!selection.client) return undefined
    return `[ctx] client_code=${selection.client.client_code}`
  }, [selection])

  const buildVisibleMessageLine = useCallback((): string | undefined => {
    if (!selection.client) return undefined
    return `Clienti allegati: ${selection.client.client_code} (PRIVATE BANKER)`
  }, [selection])

  const buildMessageMetadata = useCallback(() => {
    if (!selection.client) return undefined
    return {
      chat_context: {
        client_code: selection.client.client_code,
      },
    }
  }, [selection])

  const hasSelection = selection.client !== null

  return useMemo<ChatContextSelectionApi>(
    () => ({
      selection,
      setClient,
      clearAll,
      hasSelection,
      buildVisibleMessageLine,
      buildSeedContextLine,
      buildMessageMetadata,
    }),
    [
      selection,
      setClient,
      clearAll,
      hasSelection,
      buildVisibleMessageLine,
      buildSeedContextLine,
      buildMessageMetadata,
    ],
  )
}
